import { useAuthStore, parseJwtClaims } from '@/stores/authStore'
import { authApi } from '@/api/clients/authApi'
import { isEmbedded } from '@/lib/embed'
import { requestTokenFromParent, markEmbedSessionLost } from '@/lib/embedBridge'
import type { ApiResponse } from '@/api/types'

// In development: relative paths → Vite dev proxy → backend.
// In production: set VITE_BASE_URL to the backend origin when the frontend and
// API are on different origins. Leave empty for same-origin deployments.
const BASE = import.meta.env.VITE_BASE_URL ?? ''

// ── Token refresh mutex ───────────────────────────────────────────────────────
// Prevents multiple concurrent 401 responses from each triggering their own
// refresh request. All callers share the same in-flight promise.

let refreshPromise: Promise<string> | null = null

async function getFreshToken(): Promise<string> {
  // Embedded: the partner parent owns refreshing — ask it, and never redirect
  // the frame to the sign-in page.
  if (isEmbedded()) {
    if (!refreshPromise) {
      refreshPromise = requestTokenFromParent()
        .catch((err) => {
          markEmbedSessionLost()
          throw err
        })
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  const store = useAuthStore.getState()
  if (!store.refreshToken) {
    // Nothing to refresh with. A VITE_API_TOKEN session runs without one by
    // design, so surface the 401 to the caller rather than
    // signing the user out — the module renders its "Discovery not connected"
    // state from it. A real sign-in always carries a refresh token.
    throw new Error('[401] Unauthorized')
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const data = await authApi.refresh(store.refreshToken!)
      const claims = parseJwtClaims(data.access_token)
      store.setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        userId: claims.uid,
        orgId: claims.oid,
        role: claims.role,
      })
      return data.access_token
    })()
      .catch((err) => {
        store.clearSession()
        throw err
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

// ── Base HTTP client ──────────────────────────────────────────────────────────
// Unwraps the { success, data, error } envelope and returns `data`.

// A failed call. The message keeps the `[status] text` form callers match on;
// code and data carry the envelope's error code and any data the backend sent
// with it (a 409 BLOCKING_CHECKS lists the failing check codes there).
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
    public readonly data: unknown = undefined
  ) {
    super(`[${status}] ${message}`)
    this.name = 'ApiError'
  }
}

export const isApiError = (e: unknown, code?: string): e is ApiError => e instanceof ApiError && (code === undefined || e.code === code)

async function unwrap<T>(resp: Response): Promise<T> {
  if (resp.status === 204) return undefined as T
  const body = (await resp.json().catch(() => ({}))) as ApiResponse<T>
  if (!resp.ok || body.success === false) {
    throw new ApiError(resp.status, body?.error?.code ?? null, body?.error?.message ?? resp.statusText, body?.data)
  }
  // Envelope shape is { success, data }. When present, return data verbatim — even
  // if null (empty list serializes data:null; must NOT fall back to the envelope).
  // Only unwrap-to-body when there is no envelope at all.
  return (body && typeof body === 'object' && 'data' in body ? body.data : body) as T
}

// List endpoints serialize an empty result as data:null — coerce to [] so callers
// get a real array (empty states render, .map/.length are always safe).
export async function requestList<T>(endpoint: string, options: RequestInit = {}): Promise<T[]> {
  const r = await request<T[] | null>(endpoint, options)
  return Array.isArray(r) ? r : []
}

export async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const store = useAuthStore.getState()
  const headers: Record<string, string> = {
    // Skip Content-Type for FormData — let the browser set the multipart boundary.
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  }
  if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`

  const response = await fetch(`${BASE}${endpoint}`, { ...options, headers })

  if (response.status === 401) {
    const freshToken = await getFreshToken()
    const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` }
    const retry = await fetch(`${BASE}${endpoint}`, { ...options, headers: retryHeaders })
    return unwrap<T>(retry)
  }

  return unwrap<T>(response)
}

// Raw text/stream fetch (executive report, SSE chat) — bypasses envelope unwrap.
export async function rawRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const store = useAuthStore.getState()
  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  }
  if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`

  const response = await fetch(`${BASE}${endpoint}`, { ...options, headers })
  if (response.status === 401) {
    const freshToken = await getFreshToken()
    const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` }
    return fetch(`${BASE}${endpoint}`, { ...options, headers: retryHeaders })
  }
  return response
}

export function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return ''
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&')
  return q ? `?${q}` : ''
}

// All qtz-readiness endpoints are org-scoped under /api/v1/organizations/:orgId.
// Every Quantizant service uses this spelling; qtz-account is the exception only
// in its prefix, serving /account rather than an org path (see accountApi).
export const orgBase = (orgId: string) => `/api/v1/organizations/${orgId}`
